import { MediaEditor } from '../components/MediaEditor';
import { RHFSelect } from '@client/new/components/rhfSelect';
import { Input } from '@design-system/components/input/input';
import { Textarea } from '@design-system/components/textarea/textarea';
import { Typography } from '@design-system/components/typography/typography';
import { Controller, useWatch } from 'react-hook-form';
import { WorkoutSwapsToggle } from '@client/new/features/exercise-swaps';
import { EditWorkoutVM } from '../view-models/workoutEdit.viewModel';
import { SectionHeader } from '../components/SectionHeader';
import { WorkoutType } from '../models/workout.domain';
import { workoutTypeIcon } from '../components/workoutTypeIcon';
import { Tags } from '../components/Tags';
import { InputNumber } from '@design-system/components/input/inputNumber';
import { IntroTooltip } from '../components/TooltipContent';
import {
  isEmptyRestDay,
  maxWorkoutDurationMinutes,
} from '../models/workout.domain';
import { Equipment } from '../components/Equipment';
import { sprinkles } from '@design-system/sprinkles.css';

type WorkoutInfoFormProps = Pick<
  EditWorkoutVM,
  | 'control'
  | 'dispatch'
  | 'influencerId'
  | 'submitting'
  | 'programs'
  | 'programsLoading'
  | 'intro'
  | 'thumbnail'
  | 'tagOptions'
  | 'readonly'
  | 'workoutInfoId'
  | 'withSwaps'
>;

const sectionTitleByType: Record<WorkoutType, string> = {
  workout: 'Workout info',
  class: 'Class info',
  rest: 'Rest Day info',
};

export const WorkoutInfoForm = ({
  control,
  dispatch,
  influencerId,
  submitting,
  programs,
  programsLoading,
  intro,
  thumbnail,
  tagOptions,
  readonly,
  workoutInfoId,
  withSwaps,
}: WorkoutInfoFormProps) => {
  const programId = useWatch({ control, name: 'programId' });
  const workoutType = useWatch({ control, name: 'type' });
  const exercises = useWatch({ control, name: 'exercises' });
  const durationRequired = !isEmptyRestDay({ type: workoutType, exercises });
  return (
    <div className="flex flex-col gap-6" id={workoutInfoId}>
      <div>
        <SectionHeader
          title={sectionTitleByType[workoutType]}
          icon={workoutTypeIcon[workoutType]}
          iconTestId={`workout-editor-header-icon-${workoutType}`}
        />
        <div className="bg-white w-full flex flex-col md:flex-row p-4 gap-6 rounded-b-xl">
          <div className="flex gap-6 flex-col md:flex-row">
            <div className="flex flex-col">
              <div className="flex justify-between">
                <Typography>Intro video</Typography>
                <IntroTooltip />
              </div>
              <MediaEditor
                format="video"
                accept="video/*"
                {...intro}
                onUpload={(file) => dispatch('uploadMedia', file)}
                onRemove={() => dispatch('removeIntro')}
                disabled={readonly}
              />
            </div>
            <div className="flex flex-col">
              <div className="flex">
                <Typography>Thumbnail</Typography>
              </div>
              <MediaEditor
                format="image"
                accept="image/*"
                {...thumbnail}
                onUpload={(file) => dispatch('uploadMedia', file)}
                onRemove={() => dispatch('removeThumbnail')}
                disabled={readonly}
              />
            </div>
          </div>
          <div className="flex w-full gap-6 flex-col md:flex-row">
            <div className="flex flex-col gap-2 w-full">
              <Controller
                name={'name'}
                control={control}
                render={({ field: { onChange, value }, fieldState }) => (
                  <Input
                    label="Title"
                    required
                    disabled={submitting || readonly}
                    value={value}
                    onChange={onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name={'description'}
                control={control}
                render={({ field: { onChange, value }, fieldState }) => (
                  <Textarea
                    label="Description"
                    required
                    rows={4}
                    disabled={submitting || readonly}
                    value={value}
                    onChange={onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name={'messagePush'}
                control={control}
                render={({ field: { onChange, value }, fieldState }) => (
                  <Input
                    label="Notification message"
                    disabled={submitting || readonly}
                    value={value}
                    onChange={onChange}
                    placeholder="Enter message"
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>
            <div className="flex flex-col gap-4 w-full">
              <div className="flex flex-col">
                <div className="flex">
                  <Typography variant="body" size="medium" className="p-1">
                    Collection
                  </Typography>
                  <Typography
                    variant="body"
                    size="medium"
                    className={sprinkles({
                      paddingTop: 's',
                      color: 'ERROR-100',
                    })}
                  >
                    *
                  </Typography>
                </div>
                <RHFSelect
                  name="programId"
                  control={control}
                  options={programs}
                  required
                  hasError={!!control._formState?.errors?.programId}
                  disabled={readonly}
                  loading={programsLoading}
                />
              </div>
              <div className="flex">
                <Controller
                  name={'duration'}
                  control={control}
                  render={({ field: { onChange, value }, fieldState }) => (
                    <InputNumber
                      label="Duration"
                      disabled={submitting || readonly}
                      required={durationRequired}
                      value={value}
                      onChange={(newValue) => onChange(newValue)}
                      min={0}
                      max={maxWorkoutDurationMinutes}
                      endCta={<Typography>min</Typography>}
                      placeholder="Enter duration"
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
              <Controller
                name={'requiredEquipment'}
                control={control}
                render={({ field: { onChange, value }, fieldState }) => (
                  <Equipment
                    disabled={submitting || readonly}
                    value={value}
                    onChange={onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name={'tags'}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Tags
                    options={tagOptions}
                    tags={value}
                    onChange={onChange}
                    disabled={readonly}
                  />
                )}
              />
              {withSwaps && (
                <Controller
                  name={'swapsEnabled'}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <WorkoutSwapsToggle
                      value={value}
                      onChange={onChange}
                      influencerId={influencerId}
                      programId={programId}
                      disabled={submitting || readonly}
                    />
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
